"use client";

import { useState, useEffect, useRef } from "react";
import { useMotionMode } from "@/context/MotionContext";
import { Magnetic } from "@/components/ui/Magnetic";
import { useLetterGlitch } from "@/hooks/useLetterGlitch";
import { getScrimColor } from "@/utils/colors";
import { Footer } from "@/components/sections/Footer";
import { Tooltip } from "@/components/ui/Tooltip";
import { TopScrim } from "@/components/ui/TopScrim";

// =============================================================================
// MANIFESTO OVERLAY
// =============================================================================
// Massive floating text in the void. Scrolls independently of canvas.
// Fades behind hero text with inverted radial gradient.

const MANIFESTO_LINES: { text: string; style: "gold" | "primary" | "secondary"; glitchWord?: string }[] = [
  { text: "things change, but craft", style: "secondary" },
  { text: "stands the test of time:", style: "secondary" },
  { text: "test it, ship it, scale it.", style: "gold" },
  { text: "sometimes for screens,", style: "primary" },
  { text: "always for humans.", style: "gold" },
  { text: "foundation first,", style: "secondary" },
  { text: "soul second.", style: "secondary" },
];

interface ManifestoOverlayProps {
  isActive: boolean;
  z1Height: number;
  z2Height: number;
  crtEnabled?: boolean;
  isLightMode?: boolean;
  onScrolledChange?: (scrolled: boolean) => void;
  onAtBottomChange?: (atBottom: boolean) => void;
}

export function ManifestoOverlay({ isActive, z1Height, z2Height, crtEnabled = true, isLightMode = false, onScrolledChange, onAtBottomChange }: ManifestoOverlayProps) {
  const scrimColor = getScrimColor(isLightMode);
  const { canAnimate } = useMotionMode();
  const [showLines, setShowLines] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // Controls mount/unmount after fade
  const [isScrolled, setIsScrolled] = useState(false); // Show top scrim only when text is behind hero
  const [isAtBottom, setIsAtBottom] = useState(false); // Hide bottom scrim when scrolled to end
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle visibility with fade timing
  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
      const timer = setTimeout(() => setShowLines(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowLines(false);
      // Short delay to allow fade out
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsScrolled(false); // Reset for next open
        setIsAtBottom(false); // Reset for next open
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  // Manual scroll handling - native scroll blocked by something
  useEffect(() => {
    if (!isActive || !isVisible || !containerRef.current) return;
    const c = containerRef.current;

    const checkScrollState = () => {
      // Show top scrim when scrolled past threshold (text is behind hero)
      const scrolled = c.scrollTop > 20;
      setIsScrolled(scrolled);
      // 50px threshold for "close enough" to bottom
      const atBottom = c.scrollTop + c.clientHeight >= c.scrollHeight - 50;
      setIsAtBottom(atBottom);
    };

    const onWheel = (e: WheelEvent) => {
      // Only handle wheel if cursor is within manifesto bounds
      const rect = c.getBoundingClientRect();
      if (e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom) {
        e.preventDefault();
        c.scrollTop += e.deltaY;
        checkScrollState();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const scrollAmount = 80; // Pixels per key press
      if (e.key === "ArrowDown") {
        e.preventDefault();
        c.scrollTop += scrollAmount;
        checkScrollState();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        c.scrollTop -= scrollAmount;
        checkScrollState();
      }
    };

    const onScroll = () => {
      checkScrollState();
    };

    // Initial check
    checkScrollState();

    // Listen on window since container is pointer-events-none
    window.addEventListener('wheel', onWheel, { passive: false });
    c.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('wheel', onWheel);
      c.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isActive, isVisible]);

  // Notify parent when scroll state changes
  useEffect(() => {
    onScrolledChange?.(isScrolled);
  }, [isScrolled, onScrolledChange]);

  // Notify parent when at bottom changes
  useEffect(() => {
    onAtBottomChange?.(isAtBottom);
  }, [isAtBottom, onAtBottomChange]);

  if (!isVisible) return null;

  return (
    <>
      {/* Side click zones removed - orbs in BootStarfield handle clicks directly */}

      <TopScrim isActive={isActive} isScrolled={isScrolled} z1Height={z1Height} isLightMode={isLightMode} />

      {/* Bottom scrim - overlays overflowed manifesto text. Hides when scrolled to bottom. */}
      <div
        className="fixed left-0 right-0 z-[15] pointer-events-none"
        style={{
          bottom: 0,
          height: 200,
          background: `linear-gradient(to top, rgba(${scrimColor},1) 0%, rgba(${scrimColor},0.8) 30%, rgba(${scrimColor},0) 100%)`,
          opacity: isActive && !isAtBottom ? 1 : 0,
          transition: canAnimate ? `opacity ${isActive ? 400 : 150}ms ease-out` : "none",
        }}
      />

      {/* Scrollable manifesto container - above canvas, below hero */}
      {/* Scrollable manifesto container - pointer-events-none so clicks pass through to canvas */}
      <div
        ref={containerRef}
        data-manifesto
        className="fixed left-0 right-0 z-[14] overscroll-contain"
        style={{
          top: z1Height,
          height: `calc(100vh - ${z1Height}px)`,
          overflowY: "scroll",
          pointerEvents: isActive ? "auto" : "none",
          // Fade mask at top and bottom for clean edges
          maskImage: "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
          // Fast exit (150ms), smooth enter (400ms)
          opacity: isActive ? 1 : 0,
          transition: canAnimate ? `opacity ${isActive ? 400 : 150}ms ease-out` : "none",
        }}
      >
        {/* Spacer - positions content at ~50vh like blog feed */}
        <div style={{ height: Math.max(16, Math.round(z2Height * 0.5 - z1Height * 0.5 + 10)) }} />

        {/* Manifesto lines - each line equal spacing */}
        <div className="flex flex-col items-center text-center px-3 gap-4">
          {MANIFESTO_LINES.map((line, index) => (
            <ManifestoLine
              key={index}
              text={line.text}
              style={line.style}
              glitchWord={line.glitchWord}
              isVisible={showLines}
              delay={200 + index * 120}
              canAnimate={canAnimate}
              crtEnabled={crtEnabled}
            />
          ))}
        </div>

        {/* About Me section */}
        <AboutMe
          isVisible={showLines}
          canAnimate={canAnimate}
          delay={200 + MANIFESTO_LINES.length * 120 + 300}
        />

        {/* Cockpit footer */}
        <div
          className="px-3 sm:px-6 pb-12 pointer-events-auto"
          style={{
            opacity: showLines ? 1 : 0,
            transform: showLines ? "translateY(0)" : "translateY(20px)",
            transition: canAnimate
              ? `opacity 500ms ease-out ${200 + MANIFESTO_LINES.length * 120 + 600}ms, transform 600ms ease-out ${200 + MANIFESTO_LINES.length * 120 + 600}ms`
              : "none",
          }}
        >
          <Footer variant="cockpit" />
        </div>
      </div>
    </>
  );
}

// =============================================================================
// MANIFESTO LINE - Massive kinetic text
// =============================================================================
interface ManifestoLineProps {
  text: string;
  style: "gold" | "primary" | "secondary";
  glitchWord?: string;
  isVisible: boolean;
  delay: number;
  canAnimate: boolean;
  crtEnabled?: boolean;
}

function ManifestoLine({ text, style, glitchWord, isVisible, delay, canAnimate, crtEnabled = true }: ManifestoLineProps) {
  const goldColor = "#D4AF37";
  const primaryColor = "rgba(var(--foreground-rgb),1)";
  const secondaryColor = "rgba(var(--foreground-rgb),0.5)";

  // Glitch effect for specific word (disabled when CRT is off)
  const { text: glitchedWord } = useLetterGlitch(glitchWord || "", { idleGlitch: true, intervalMultiplier: 0.5, enabled: crtEnabled });

  const color = style === "gold" ? goldColor : style === "primary" ? primaryColor : secondaryColor;
  const isGold = style === "gold";

  // Render text with optional glitched word
  const renderText = () => {
    if (!glitchWord) return text;

    const parts = text.split(glitchWord);
    return (
      <>
        {parts[0]}{glitchedWord}{parts[1]}
      </>
    );
  };

  const content = (
    <p
      className="text-[clamp(1.8rem,8vw,6rem)] font-black font-headline leading-[1.15] tracking-tight whitespace-nowrap select-none pointer-events-none"
      style={{
        color,
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? crtEnabled ? "perspective(400px) rotateX(10deg) translateY(0) scale(1)" : "translateY(0) scale(1)"
          : crtEnabled ? "perspective(400px) rotateX(10deg) translateY(40px) scale(0.95)" : "translateY(40px) scale(0.95)",
        transformOrigin: "bottom center",
        transition: canAnimate
          ? `opacity 500ms ease-out ${delay}ms, transform 700ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`
          : "none",
        textShadow: isGold
          ? "0 0 60px rgba(212, 175, 55, 0.3)"
          : crtEnabled
            ? "var(--crt-text-shadow)"
            : "none",
      }}
    >
      {renderText()}
    </p>
  );

  if (isGold) {
    return (
      <Magnetic strength={0.15} tiltStrength={8} className="pointer-events-auto">
        {content}
      </Magnetic>
    );
  }

  return content;
}

// =============================================================================
// ABOUT ME - Bio section below manifesto
// =============================================================================
interface AboutMeProps {
  isVisible: boolean;
  canAnimate: boolean;
  delay: number;
}

function AboutMe({ isVisible, canAnimate, delay }: AboutMeProps) {
  const goldColor = "#D4AF37";

  const handleThirdEyeClick = () => {
    // Navigate to contact system
    window.dispatchEvent(new Event("open-contact"));
    // After navigation settles, auto-focus the TEC orb
    setTimeout(() => {
      window.dispatchEvent(new Event("focus-contact-tec"));
    }, 300);
  };

  return (
    <div
      className="max-w-4xl mx-auto px-3 sm:px-6 pt-24 pb-32 text-center select-none"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transition: canAnimate
          ? `opacity 600ms ease-out ${delay}ms, transform 800ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`
          : "none",
      }}
    >
      {/* Divider */}
      <div
        className="w-12 h-px mx-auto mb-12"
        style={{ background: `linear-gradient(90deg, transparent, ${goldColor}, transparent)` }}
      />

      {/* Bio paragraphs */}
      <p className="text-base md:text-lg leading-relaxed text-[rgba(var(--foreground-rgb),0.9)] mb-6 pointer-events-none">
        Vincent Ngu designs how <strong className="font-semibold text-[rgba(var(--foreground-rgb),1)]">millions of people watch anime</strong>.
        He leads playback design at Crunchyroll across web, mobile, living room, and spatial.
        He shipped the <strong className="font-semibold text-[rgba(var(--foreground-rgb),1)]">top-rated streaming app on Apple Vision Pro</strong> and is currently redesigning the
        core player serving <strong className="font-semibold text-[rgba(var(--foreground-rgb),1)]">21 million subscribers</strong> worldwide.
      </p>

      <p className="text-base md:text-lg leading-relaxed text-[rgba(var(--foreground-rgb),0.7)] mb-6">
        <span className="pointer-events-none">
          His work spans edtech, travel, conversational AI, sports betting, e-commerce, fast casual,
          and entertainment. Different industries, same instinct: find the system underneath the surface
          and <span style={{ color: goldColor }}>make it work for the person using it.</span> He also coaches designers through career inflection
          points and organizational politics at{" "}
        </span>
        <Tooltip
          text="Career coaching for designers →"
          position="top"
          icon={
            // eslint-disable-next-line @next/next/no-img-element
            <img src="/images/icon/TE-Icon.webp" alt="Third Eye" width={20} height={20} style={{ borderRadius: 4 }} />
          }
        >
          <button
            onClick={handleThirdEyeClick}
            className="gold-gradient-text cursor-pointer pointer-events-auto font-semibold"
            style={{ borderBottom: `2px solid ${goldColor}` }}
          >
            Third Eye
          </button>
        </Tooltip>
        <span className="pointer-events-none">.</span>
      </p>

      <p className="text-base md:text-lg leading-relaxed text-[rgba(var(--foreground-rgb),0.55)] pointer-events-none">
        Away from the screen, he mentors a kid through{" "}
        <span className="text-[rgba(var(--foreground-rgb),0.7)]">Big Brothers Big Sisters</span>{" "}
        and is teaching himself Blender, translating 2D instincts into three dimensions.
        He recharges through Soulsborne games, Balatro, anime, manga, and travel in search of great food and new ink.
      </p>
    </div>
  );
}
